/**
 * Measuring Angles generator (6.MGSR.2.1).
 *
 * Read the degree measure of an angle. The measure is chosen first and an
 * angle-diagram graph is built with rays at 0 and the measure, so the drawn
 * figure always matches the graded answer. Measures are whole numbers.
 *
 * Families:
 *   F1: acute   (0 < m < 90)
 *   F2: obtuse  (90 < m < 180)
 *   F3: right   (m = 90)
 *   F4: straight-ish / large (120 <= m <= 180)
 */

import { rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type { AngleDiagramModel } from "../graph/plot-model";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "measuring-angles" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  /** Step used when picking measures (larger step = rounder numbers). */
  readonly step: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F3"], step: 10 },
  standard: { families: ["F1", "F2", "F3"], step: 5 },
  hard: { families: ["F1", "F2", "F3", "F4"], step: 5 },
  challenge: { families: ["F1", "F2", "F4"], step: 1 },
};

/** Pick a whole-number measure in [min, max] snapped to `step`. */
function pickMeasure(rng: Rng, min: number, max: number, step: number): number {
  const lo = Math.ceil(min / step);
  const hi = Math.floor(max / step);
  return rng.int(lo, hi) * step;
}

function angleName(m: number): string {
  if (m < 90) return "acute";
  if (m === 90) return "right";
  if (m < 180) return "obtuse";
  return "straight";
}

export class MeasuringAnglesGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      let measure: number;
      switch (family) {
        case "F1":
          measure = pickMeasure(rng, 10, 85, cfg.step);
          break;
        case "F2":
          measure = pickMeasure(rng, 95, 175, cfg.step);
          break;
        case "F3":
          measure = 90;
          break;
        case "F4":
          measure = pickMeasure(rng, 120, 180, cfg.step);
          break;
      }

      const answer = rat(measure);
      const graph: AngleDiagramModel = {
        kind: "angle-diagram",
        rays: [0, measure],
        label: `${measure}\u00b0`,
      };

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: `The angle shown is ${angleName(measure)}. What is its measure, in degrees?`,
        },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps: [
          {
            description:
              "Read the measure where the second ray crosses the protractor scale, starting from the first ray.",
            latex: `${measure}^{\\circ}`,
          },
        ],
        hints: [
          { level: 1, text: "Line the vertex up with the protractor's center and one ray on 0\u00b0." },
          {
            level: 2,
            text: "Acute angles are less than 90\u00b0, right angles are exactly 90\u00b0, and obtuse angles are between 90\u00b0 and 180\u00b0.",
          },
        ],
        subskills: topic.subskills,
        graph,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const measuringAngles = new MeasuringAnglesGenerator();
